/**
 * TransferModal
 *
 * Moves one student from their current class to another.
 *
 * Three steps, because a transfer moves money and the admin needs to see
 * what it will do BEFORE it does it:
 *
 *   1. pick the target class
 *   2. read the preview — cost, guardrails, and what changes on the bill
 *   3. confirm (with a reason, if an owner is overriding a guardrail)
 *
 * The preview comes from the server and is the same evaluation the execute
 * endpoint runs, so what is shown here cannot disagree with what happens.
 */

import React, { useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Ban,
  Info,
  Loader2,
  X,
} from "lucide-react";
import enrollmentsService from "../../api/services/enrollments.service";

const money = (v) => `$${Math.abs(parseFloat(v ?? 0)).toFixed(2)}`;

/** The one-line answer to "what does this cost?" */
function netSummary(preview) {
  const net = parseFloat(preview.net_amount ?? 0);
  if (net < 0) {
    return {
      tone: "credit",
      text: `${money(net)} credit to the family's account`,
    };
  }
  if (net === 0) return { tone: "even", text: "Nothing further to pay" };
  const how =
    preview.settlement_method === "card"
      ? "charged to the card on file"
      : preview.settlement_method === "invoice"
        ? "invoiced, 7-day terms"
        : "due";
  return { tone: "charge", text: `${money(net)} ${how}` };
}

export default function TransferModal({
  isOpen,
  onClose,
  onConfirm,
  enrollment,
  classes = [],
  isOwner = false,
  isSubmitting = false,
}) {
  const [targetClassId, setTargetClassId] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [overrideReason, setOverrideReason] = useState("");

  // Reset whenever the modal opens for a different enrollment.
  useEffect(() => {
    if (!isOpen) return;
    setTargetClassId("");
    setPreview(null);
    setPreviewError("");
    setOverrideReason("");
  }, [isOpen, enrollment?.id]);

  // Re-quote whenever the target changes. The endpoint is read-only, so
  // there is no cost to the admin browsing several options.
  useEffect(() => {
    if (!isOpen || !targetClassId || !enrollment?.id) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setLoadingPreview(true);
    setPreviewError("");
    enrollmentsService
      .previewTransfer(enrollment.id, targetClassId)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setPreviewError(
            err?.message || "Could not price this transfer. Try again.",
          );
          setPreview(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, targetClassId, enrollment?.id]);

  if (!isOpen) return null;

  const blocks = preview?.blocks ?? [];
  const warnings = preview?.warnings ?? [];
  const canOverride = preview?.requires_owner_override && isOwner;
  const needsReason = canOverride && !overrideReason.trim();
  const canSubmit =
    !!preview &&
    !loadingPreview &&
    !isSubmitting &&
    (preview.can_transfer || (canOverride && !needsReason));

  const summary = preview ? netSummary(preview) : null;

  const availableClasses = classes.filter(
    (c) => c.id !== enrollment?.class_id,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center shrink-0">
              <ArrowRight className="w-5 h-5 text-[#F3BC48]" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold font-manrope text-[#173151] truncate">
                Transfer Class
              </h3>
              <p className="text-xs font-manrope text-[#666D80] truncate">
                {enrollment?.child_name} &middot; currently in{" "}
                {enrollment?.class_name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1 hover:bg-gray-100 rounded transition shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Step 1 — target class */}
          <div>
            <label
              htmlFor="transfer-target"
              className="block text-sm font-manrope font-semibold text-[#173151] mb-2"
            >
              Move to
            </label>
            <select
              id="transfer-target"
              value={targetClassId}
              onChange={(e) => setTargetClassId(e.target.value)}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-manrope focus:ring-2 focus:ring-[#F3BC48] focus:border-[#F3BC48]"
            >
              <option value="">Select a class…</option>
              {availableClasses.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.current_enrollment || 0}/{c.capacity})
                </option>
              ))}
            </select>
          </div>

          {loadingPreview && (
            <div className="flex items-center gap-2 text-sm font-manrope text-[#666D80]">
              <Loader2 className="w-4 h-4 animate-spin" />
              Pricing this transfer…
            </div>
          )}

          {previewError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm font-manrope text-red-700">
              {previewError}
            </div>
          )}

          {/* Step 2 — the preview */}
          {preview && !loadingPreview && (
            <>
              {/* Money */}
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <div className="flex justify-between text-sm font-manrope">
                  <span className="text-[#666D80]">
                    Credit from {preview.source_class_name}
                  </span>
                  <span className="text-[#173151] font-semibold">
                    {money(preview.credit_amount)}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-manrope">
                  <span className="text-[#666D80]">
                    Charge for {preview.target_class_name}
                  </span>
                  <span className="text-[#173151] font-semibold">
                    {money(preview.new_charge_amount)}
                  </span>
                </div>
                <div className="pt-2 border-t border-gray-200 flex justify-between text-sm font-manrope">
                  <span className="text-[#173151] font-semibold">Net</span>
                  <span
                    className={`font-bold ${
                      summary.tone === "credit"
                        ? "text-green-700"
                        : summary.tone === "charge"
                          ? "text-[#173151]"
                          : "text-[#666D80]"
                    }`}
                  >
                    {summary.text}
                  </span>
                </div>
              </div>

              {/* Billing change — the line that prevents the support call */}
              {preview.new_monthly && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm font-manrope text-[#173151]">
                  Going forward: <strong>${preview.new_monthly}/month</strong>
                  {preview.current_monthly &&
                    preview.current_monthly !== preview.new_monthly && (
                      <span className="text-[#666D80]">
                        {" "}
                        (was ${preview.current_monthly})
                      </span>
                    )}
                </div>
              )}

              {preview.source_completion_percent !== null && (
                <p className="text-xs font-manrope text-[#666D80]">
                  {preview.source_class_name} is{" "}
                  {preview.source_completion_percent}% complete.
                </p>
              )}

              {/* Blocks */}
              {blocks.map((b) => (
                <div
                  key={b.code}
                  className="p-3 bg-red-50 border border-red-200 rounded-lg flex gap-2"
                >
                  <Ban className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                  <p className="text-sm font-manrope text-red-700">
                    {b.message}
                  </p>
                </div>
              ))}

              {/* Warnings */}
              {warnings.map((w) => (
                <div
                  key={w.code}
                  className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-2"
                >
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-sm font-manrope text-amber-800">
                    {w.message}
                  </p>
                </div>
              ))}

              {/* Step 3 — override */}
              {preview.requires_owner_override && !isOwner && (
                <div className="p-3 bg-gray-100 border border-gray-200 rounded-lg flex gap-2">
                  <Info className="w-4 h-4 text-gray-500 shrink-0 mt-0.5" />
                  <p className="text-sm font-manrope text-[#666D80]">
                    An owner can override this and complete the transfer.
                  </p>
                </div>
              )}

              {canOverride && (
                <div>
                  <label
                    htmlFor="override-reason"
                    className="block text-sm font-manrope font-semibold text-[#173151] mb-2"
                  >
                    Reason for overriding{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="override-reason"
                    rows={2}
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder="e.g. Family relocated mid-season; coach agreed."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-manrope focus:ring-2 focus:ring-[#F3BC48] focus:border-[#F3BC48]"
                  />
                  <p className="text-xs font-manrope text-[#666D80] mt-1">
                    Recorded on the transfer and emailed to all admins.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 pt-0">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-manrope font-semibold text-[#666D80] hover:bg-gray-100 rounded-lg transition"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              onConfirm({
                targetClassId,
                overrideReason: overrideReason.trim() || null,
              })
            }
            disabled={!canSubmit}
            className="px-4 py-2 text-sm font-manrope font-semibold text-white bg-[#173151] rounded-lg transition hover:bg-[#0f2338] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isSubmitting ? "Transferring…" : "Confirm Transfer"}
          </button>
        </div>
      </div>
    </div>
  );
}
